import { capitalize, isNil, words } from "lodash-es";
import type { FieldConfig, VariableConfig } from "../fields/types";
import { toField } from "../fields/fields";

export const sanitizeDataName = (input: string): string => {
  const sanitizedAlphanumeric = input
    .normalize("NFD")
    .replaceAll(/[^\p{L}0-9\s_]+/gu, "") // Remove everything that's not a letter, digit, space, or underscore
    .replace(/^[\d\s]+/, ""); // Remove leading numbers and spaces

  // Camelcase each underscore-separated segment (leaving the first word untouched),
  // then re-join with underscores, so they are preserved in the result.
  return sanitizedAlphanumeric
    .split("_")
    .map((segment) =>
      words(segment)
        .map((word, index) => (index === 0 ? word : capitalize(word.toLowerCase())))
        .join(""),
    )
    .join("_");
};

export const createFieldDataNameMap = (fields: FieldConfig[]): Map<string, FieldConfig[]> => {
  const map = new Map<string, FieldConfig[]>();

  fields.forEach((field) => {
    const dataName = field.properties.data_name as string | undefined;
    if (isNil(dataName) || !toField(field.type).hasData) {
      return;
    }
    const key = `${field.scope.toString()}:${dataName.toLowerCase()}`;
    const existing = map.get(key) ?? [];
    existing.push(field);
    map.set(key, existing);
  });

  return map;
};

export const createVariableDataNameMap = (variables: VariableConfig[]): Map<string, VariableConfig[]> => {
  const map = new Map<string, VariableConfig[]>();

  variables.forEach((variable) => {
    if (isNil(variable.dataName)) {
      return;
    }
    const key = variable.dataName.toLowerCase();
    const existing = map.get(key) ?? [];
    existing.push(variable);
    map.set(key, existing);
  });

  return map;
};
